import React, { useMemo, useCallback } from 'react';
import { isEqual, round } from 'lodash';
import { observer } from 'mobx-react-lite';
import Form from 'mobx-react-form';
import { Typography, Box, Radio, FormControl, RadioGroup, FormControlLabel, FormLabel } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';

import { divide } from 'shared/utils/numberOperations';
import { expenseFormHelpers } from '../operatingExpenseFormConfig';
import { Table } from 'components/shared/Table';
import { calculateOperatingExpenses, getBaseColumns } from './helpers';
import { EXPENSE_RATIO_LABEL, EXPENSE_AMOUNT_LABEL, EXPENSE_INPUT_METHOD_RADIO_BUTTON_ITEMS } from '../../constants';
import { EXPENSE_HISTORY_TABLE_BASE_SX } from './constants';
import { incomeApproachCalculations } from 'shared/calculations/incomeApproach';
import { useRootStore } from 'stores/RootStore';
import { UnderwritingOutput } from 'types/underwriting/underwriting';
import { convertUndefinedToNull } from 'components/underwriting/utils/helpers';
import { ExpenseHistory } from 'types/underwriting/income-approach/expenses';
import { EXPENSE_AMOUNT_INPUT_METHOD } from 'shared/constants/expenses';

interface SummarizedExpenseHistoryTableProps {
  form: Form;
}

interface ExpenseRow {
  id: string;
  category: string;
  isTotal?: boolean;
  average: number;
  [key: string]: any;
}

const SummarizedExpenseHistoryTable = observer(({ form }: SummarizedExpenseHistoryTableProps) => {
  const { underwritingPageStore } = useRootStore();
  const { underwritingData = {} as UnderwritingOutput } = underwritingPageStore;

  const expenseHistoryField = form.$('incomeApproach.expenses.expenseHistory');
  const inputMethodField = form.$('incomeApproach.expenses.expenseHistoryInputMethod');

  const effectiveGrossIncome = useMemo(
    () => incomeApproachCalculations.calculateEffectiveGrossIncome(underwritingData),
    [underwritingData]
  );

  const expenseHistory = expenseHistoryField.value || [];
  const inputMethod = inputMethodField.value;

  const deleteColumn = useCallback(
    (expenseHistoryId: string) => {
      expenseFormHelpers.removeExpenseYear(form, expenseHistoryId);
    },
    [form]
  );

  const handleProcessRowUpdate = useCallback(
    (newRow: any, oldRow: any) => {
      if (newRow.isTotal || isEqual(newRow, oldRow)) {
        return oldRow;
      }

      const rowValues: number[] = [];

      expenseHistory.forEach((expenseHistoryItem: any) => {
        const fieldName = `${expenseHistoryItem.period}-${expenseHistoryItem.year}`;

        if (newRow[fieldName] !== oldRow[fieldName]) {
          const newValue = newRow[fieldName];
          const sanitizedValue = !isNaN(newValue) ? Math.abs(round(newValue, 4)) : newValue;

          const expenseHistoryField = form.$('incomeApproach.expenses.expenseHistory');
          const currentHistory = expenseHistoryField.value || [];

          const updatedHistory = currentHistory.map((historyItem: any) => {
            if (`${historyItem.period}-${historyItem.year}` === fieldName) {
              const { amount, ratio } = calculateOperatingExpenses(effectiveGrossIncome, sanitizedValue, inputMethod);

              historyItem.totalExpenseAmount = amount;
              historyItem.operatingExpenseRatio = ratio;
            }
            return historyItem;
          });

          newRow[fieldName] = sanitizedValue;
          expenseHistoryField.set(updatedHistory);
        }

        rowValues.push(newRow[fieldName] || 0);
      });

      newRow.average = divide(
        rowValues.reduce((sum, val) => sum + val, 0),
        expenseHistory.length
      );

      return convertUndefinedToNull({ ...newRow });
    },
    [form, expenseHistory, inputMethod, effectiveGrossIncome]
  );

  const rows: ExpenseRow[] = useMemo(() => {
    const yearCount = expenseHistory.length || 1;

    const id = inputMethod === EXPENSE_AMOUNT_INPUT_METHOD ? 'operating-expenses-amount' : 'operating-expenses-ratio';
    const expenseValues: number[] = [];
    const totalValues: number[] = [];

    const expenseRow: ExpenseRow = {
      id,
      key: id,
      category: inputMethod === EXPENSE_AMOUNT_INPUT_METHOD ? EXPENSE_AMOUNT_LABEL : EXPENSE_RATIO_LABEL,
      isTotal: false,
      average: 0,
    };

    const totalOperatingExpenses: ExpenseRow = {
      id: 'total-operating-expenses',
      category: 'Total Operating Expenses',
      isTotal: true,
      average: 0,
    };

    expenseHistory.forEach((expenseHistoryItem: ExpenseHistory) => {
      const yearKey = `${expenseHistoryItem.period}-${expenseHistoryItem.year}`;

      const expenseValue =
        inputMethod === EXPENSE_AMOUNT_INPUT_METHOD
          ? expenseHistoryItem.totalExpenseAmount
          : expenseHistoryItem.operatingExpenseRatio;

      const { amount, ratio } = calculateOperatingExpenses(effectiveGrossIncome, expenseValue || 0, inputMethod);

      const totalValue = inputMethod === EXPENSE_AMOUNT_INPUT_METHOD ? ratio : amount;

      expenseRow[yearKey] = expenseValue || 0;
      totalOperatingExpenses[yearKey] = totalValue || 0;

      expenseValues.push(expenseValue || 0);
      totalValues.push(totalValue || 0);
    });

    expenseRow.average =
      divide(
        expenseValues.reduce((sum, val) => sum + val, 0),
        yearCount
      ) || 0;

    totalOperatingExpenses.average =
      divide(
        totalValues.reduce((sum, val) => sum + val, 0),
        yearCount
      ) || 0;

    return [expenseRow, totalOperatingExpenses];
  }, [effectiveGrossIncome, expenseHistory, inputMethod]);

  const columns = useMemo(() => {
    const baseColumns: GridColDef[] = getBaseColumns(expenseHistory, deleteColumn, inputMethod);
    return baseColumns;
  }, [expenseHistory, deleteColumn, inputMethod]);

  return (
    <Box sx={{ pt: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          Summarized Expense History
        </Typography>
      </Box>

      <FormControl>
        <FormLabel>Expense History Input Method</FormLabel>
        <RadioGroup
          {...form.$('incomeApproach.expenses.expenseHistoryInputMethod').bind()}
          row
          sx={{ minHeight: 40, mt: 0, mb: 2 }}
        >
          {EXPENSE_INPUT_METHOD_RADIO_BUTTON_ITEMS.map(({ value, label }) => (
            <FormControlLabel control={<Radio />} key={value} sx={{ mb: 3 / 4 }} {...{ label, value }} />
          ))}
        </RadioGroup>
      </FormControl>

      <Table
        rows={rows}
        columns={columns}
        processRowUpdate={handleProcessRowUpdate}
        onProcessRowUpdateError={(error) => console.error('Row update error:', error)}
        getRowClassName={(params) => (params.row.isTotal ? 'total-row' : '')}
        getCellClassName={(params) => {
          const { row, field } = params;
          if ((field === 'category' || field === 'average') && !row.isTotal) {
            return 'disabled-cell';
          }
          return '';
        }}
        sx={{ ...EXPENSE_HISTORY_TABLE_BASE_SX }}
        isCellEditable={(params) => {
          if (params.row.isTotal) {
            return false;
          }

          return true;
        }}
        columnHeaderHeight={undefined}
      />
    </Box>
  );
});

export default SummarizedExpenseHistoryTable;
